import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Heart, Eye, Users as UsersIcon, MessageCircle, Check, X, ExternalLink, Loader2, Building2, CreditCard, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';

const tr = (t, key, fallback) => {
  const v = t(key);
  return !v || v === key ? fallback : v;
};

const formatAmount = (amount, currency) => {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
};

export default function SponsorshipInbox() {
  const { t } = useLanguage();
  const KIND_LABEL = {
    gim_toli: tr(t, 'ads.kindGimToli', 'Gim Toli'),
    podcast: tr(t, 'ads.kindPodcast', 'Podcast'),
    short_film: tr(t, 'ads.kindShortFilm', 'Film court'),
    video: tr(t, 'ads.kindVideo', 'Vidéo partagée'),
  };
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [filter, setFilter] = useState('all');

  const reload = async () => {
    setLoading(true);
    try {
      const r = await axios.get('/api/monetization/requests/incoming');
      setRequests(r.data?.requests || []);
    } catch (_e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const decide = async (req, decision) => {
    setActing(req.id);
    try {
      await axios.post(`/api/monetization/requests/${req.id}/decision`, { decision });
      toast.success(decision === 'accept'
        ? tr(t, 'ads.sponsorAccepted', 'Sponsoring accepté')
        : tr(t, 'ads.sponsorRejected', 'Demande refusée'));
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || tr(t, 'ads.genericError', 'Erreur'));
    } finally {
      setActing(null);
    }
  };

  const payNow = async (req) => {
    setActing(req.id);
    try {
      const r = await axios.post(`/api/monetization/requests/${req.id}/checkout`);
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        toast.error(tr(t, 'ads.genericError', 'Erreur'));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || tr(t, 'ads.genericError', 'Erreur'));
    } finally {
      setActing(null);
    }
  };

  const filtered = requests.filter((r) => filter === 'all' || r.status === filter);

  return (
    <div className="space-y-4" data-testid="sponsorship-inbox">
      <div className="flex flex-wrap gap-2">
        {[
          ['all', tr(t, 'ads.filterAll', 'Tout')],
          ['pending', tr(t, 'ads.filterPending', 'En attente')],
          ['accepted', tr(t, 'ads.filterAccepted', 'Acceptées')],
          ['rejected', tr(t, 'ads.filterRejected', 'Refusées')],
        ].map(([k, l]) => (
          <button
            key={k}
            type="button"
            onClick={() => setFilter(k)}
            className={`px-3 py-1.5 rounded-full text-sm transition-colors ${filter === k ? 'bg-magenta-500 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}
            data-testid={`sponsorship-filter-${k}`}
          >
            {l}
          </button>
        ))}
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12 text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="p-8 text-center text-gray-500 bg-white/5 rounded-2xl border border-white/10">
          {tr(t, 'ads.noSponsorshipRequests', 'Aucune demande de sponsoring.')}
        </div>
      )}

      {!loading && filtered.map((r) => (
        <div key={r.id} className="p-5 rounded-2xl bg-white/5 border border-white/10" data-testid={`incoming-request-${r.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
            <div className="min-w-0">
              <div className="font-semibold text-lg">{r.creator_name}</div>
              <div className="text-xs text-gray-400">{KIND_LABEL[r.content_kind] || r.content_kind} · {tr(t, 'ads.duration', 'Durée')} {r.content_duration_sec}s</div>
            </div>
            <div className="text-right">
              <div className="text-xl font-bold text-emerald-300">{formatAmount(r.amount, r.currency)}</div>
              <div className="text-xs text-gray-400 capitalize">{r.status}</div>
            </div>
          </div>

          {/* Creator stats snapshot */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
            <Stat icon={UsersIcon} label={tr(t, 'ads.followers', 'Abonnés')} value={r.creator_followers} />
            <Stat icon={Heart} label={tr(t, 'ads.likes', 'Likes')} value={r.content_likes} />
            <Stat icon={Eye} label={tr(t, 'ads.plays', 'Lectures')} value={r.content_plays} />
          </div>

          {r.note && (
            <div className="mb-3 p-3 bg-white/5 rounded-lg text-sm text-gray-300">
              <MessageCircle className="w-3.5 h-3.5 inline mr-1.5 text-gray-500" />
              {r.note}
            </div>
          )}

          {/* Payout info shown after acceptance */}
          {r.status === 'accepted' && r.creator_payout?.iban && (
            <div className="mb-3 p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/30 text-sm">
              <div className="flex items-center gap-2 mb-1 font-semibold text-cyan-200"><Building2 className="w-4 h-4" /> {tr(t, 'ads.payoutCoords', 'Coordonnées de paiement')}</div>
              <div className="font-mono text-xs break-all">{r.creator_payout.iban}</div>
              {r.creator_payout.bic && <div className="font-mono text-xs text-gray-400">{tr(t, 'ads.bic', 'BIC')} : {r.creator_payout.bic}</div>}
              {r.creator_payout.holder_name && <div className="text-xs text-gray-400">{tr(t, 'ads.holder', 'Titulaire')} : {r.creator_payout.holder_name}</div>}
            </div>
          )}

          {/* In-app Stripe payment button (after acceptance, before payment) */}
          {r.status === 'accepted' && !r.paid && (
            <div className="mb-3">
              <button
                type="button"
                onClick={() => payNow(r)}
                disabled={acting === r.id}
                className="w-full py-2.5 rounded-full bg-gradient-to-r from-magenta-500 to-purple-600 hover:brightness-110 font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid={`pay-sponsorship-${r.id}`}
              >
                {acting === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                {tr(t, 'ads.payNow', 'Payer le créateur')} — {formatAmount(r.amount, r.currency)}
              </button>
              <p className="text-[10px] text-gray-500 mt-1.5 text-center">
                {tr(t, 'ads.payHint', 'Frais Stripe couverts par vous (~2,9 % + 0,30 €) — le créateur reçoit le montant exact.')}
              </p>
            </div>
          )}

          {/* Payment confirmed */}
          {r.status === 'accepted' && r.paid && (
            <div className="mb-3 p-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-sm flex items-center gap-2 text-emerald-200" data-testid={`paid-badge-${r.id}`}>
              <CheckCircle2 className="w-4 h-4" /> {tr(t, 'ads.paid', 'Paiement effectué')} — {formatAmount(r.amount, r.currency)}
            </div>
          )}

          {/* Actions */}
          {r.status === 'pending' && (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => decide(r, 'accept')}
                disabled={acting === r.id}
                className="flex-1 py-2.5 rounded-full bg-gradient-to-r from-emerald-500 to-green-600 font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid={`accept-sponsorship-${r.id}`}
              >
                <Check className="w-4 h-4" /> {tr(t, 'ads.accept', 'Accepter')}
              </button>
              <button
                type="button"
                onClick={() => decide(r, 'reject')}
                disabled={acting === r.id}
                className="flex-1 py-2.5 rounded-full bg-white/10 hover:bg-rose-500/30 font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid={`reject-sponsorship-${r.id}`}
              >
                <X className="w-4 h-4" /> {tr(t, 'ads.reject', 'Refuser')}
              </button>
            </div>
          )}

          {r.status === 'accepted' && r.id && <ClickStats requestId={r.id} sponsorWebsite={r.sponsor_website} />}
        </div>
      ))}
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white/5 rounded-xl p-2.5 flex items-center gap-2">
      <Icon className="w-4 h-4 text-magenta-400 flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
        <div className="text-sm font-bold tabular-nums">{(value ?? 0).toLocaleString()}</div>
      </div>
    </div>
  );
}

function ClickStats({ requestId, sponsorWebsite }) {
  const { t } = useLanguage();
  const [n, setN] = useState(null);
  useEffect(() => {
    let on = true;
    axios.get(`/api/monetization/clicks/${requestId}/count`).then((r) => { if (on) setN(r.data?.clicks || 0); }).catch(() => {});
    return () => { on = false; };
  }, [requestId]);
  return (
    <div className="mt-3 flex items-center justify-between text-sm">
      <span className="text-gray-400">{tr(t, 'ads.sponsorLinkClicks', 'Clics sur lien sponsor')} : <strong className="text-white">{n ?? '…'}</strong></span>
      {sponsorWebsite && (
        <a href={sponsorWebsite} target="_blank" rel="noopener noreferrer" className="text-magenta-300 hover:text-magenta-200 flex items-center gap-1 text-xs">
          <ExternalLink className="w-3 h-3" /> {sponsorWebsite.replace(/^https?:\/\//, '')}
        </a>
      )}
    </div>
  );
}
